/**
 * What we pay the operator on ONE slot — on the dispatcher's trip screen
 * only, never on a board row.
 *
 * The fee AS AGREED, then every adjustment to it on its own line with the
 * name whoever booked it typed, then what that comes to. Not a lump: the
 * call the office is on is "why is it 2 600 more than we agreed", and the
 * answer is the line that says `extra hour at arrivals`.
 *
 * THE NAMING IS THE DASHBOARD'S. `orderLineKindLabel` (Utils/orderLineKinds)
 * is the table the Order Ledger's own badges are drawn from, so a line reads
 * "Adjustment · extra hour at arrivals" here exactly as it does there. The
 * dashboard's MODAL could not come with it — it is a react-bootstrap
 * fullscreen dialog that fetches the ledger and the payments with the admin
 * client and opens three more admin modals out of its rows, none of which
 * exists in this shell — so what is shared is the naming, and the formatting
 * is the portal's own (`formatMoney`: '9 000 Ft', as every other portal
 * screen writes money).
 *
 * A slot nobody is on yet has no fee and shows nothing at all, rather than a
 * zero somebody might read as "free".
 */
import React from 'react';
import { formatMoney } from '../Operator/operatorFormat';
import { orderLineKindLabel } from '../../../Utils/orderLineKinds';
import { Facts } from '../Components/TripCards';

/** '+ 2 600 Ft' / '− 740 Ft' — an adjustment reads as the direction it moves. */
export const adjustmentAmount = (line) => {
  const amount = Number(line?.amount);

  if (!Number.isFinite(amount)) return '';

  return `${amount > 0 ? '+ ' : ''}${formatMoney(amount, line?.currency)}`;
};

/** 'Adjustment · extra hour at arrivals' — the kind, then what it was for. */
export const adjustmentLabel = (line) => [
  orderLineKindLabel(line?.kind),
  line?.description,
].filter(Boolean).join(' · ');

export default function OperatorFeeCard({ fee, operator = '' }) {
  const adjustments = fee?.adjustments || [];
  const agreed = fee?.operator_price;
  const hasAgreed = agreed !== undefined && agreed !== null;

  if (!hasAgreed && !adjustments.length) return null;

  return (
    <Facts
      title={operator ? `What we pay ${operator}` : 'What we pay the operator'}
      rows={[
        { key: 'Agreed fee', value: hasAgreed ? formatMoney(agreed, fee.operator_currency) : '' },
        ...adjustments.map((line, index) => ({
          // Two adjustments can carry the same words; their order is the
          // identity, exactly as it is in the ledger they came from — so it
          // is the React key and never the label on screen.
          id: `adjustment-${index}`,
          key: adjustmentLabel(line),
          value: adjustmentAmount(line),
        })),
        // Only when the API worked one out — it will not add across
        // currencies, and neither will this screen.
        {
          key: 'Total',
          value: typeof fee?.total === 'number' && adjustments.length
            ? formatMoney(fee.total, fee.operator_currency)
            : '',
        },
      ]}
    />
  );
}
